import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

const GROUPS = [
  {
    type: 'Purchase Orders',
    agents: [
      { name: 'Orchestrator Agent',              attention: false },
      { name: 'Status & Tracking',              attention: false },
      { name: 'Creation & Replenishment',        attention: false },
      { name: 'Validation',                      attention: false },
      { name: 'Exception Resolution',            attention: false },
      { name: 'PO-to-Invoice Matching',          attention: false },
    ],
  },
  {
    type: 'Pricing',
    agents: [
      { name: 'Contracts',   attention: false },
      { name: 'Spot Market', attention: false },
      { name: 'Rebates',     attention: false },
    ],
  },
  {
    type: 'Demand Planning',
    agents: [
      { name: 'Sensing',     attention: true  },
      { name: 'Forecast',    attention: false },
      { name: 'Clinical',    attention: false },
    ],
  },
  {
    type: 'Inventory',
    agents: [
      { name: 'PAR Review',  attention: true  },
      { name: 'Consignment', attention: false },
      { name: 'Expiry',      attention: false },
    ],
  },
  {
    type: 'Scheduling',
    agents: [
      { name: 'OR Confirm',  attention: true  },
      { name: 'OR Planning', attention: false },
      { name: 'Block Time',  attention: false },
    ],
  },
  {
    type: 'Contracts',
    agents: [
      { name: 'Compliance',  attention: false },
      { name: 'Renewal',     attention: false },
    ],
  },
  {
    type: 'Invoicing',
    agents: [
      { name: 'PO Match',    attention: false },
      { name: 'Credits',     attention: false },
      { name: 'Reconcile',   attention: false },
    ],
  },
  {
    type: 'Supplier',
    agents: [
      { name: 'Onboarding',  attention: false },
      { name: 'Performance', attention: false },
      { name: 'Risk',        attention: false },
      { name: 'Development', attention: false },
      { name: 'Compliance',  attention: false },
    ],
  },
  {
    type: 'Logistics',
    agents: [
      { name: 'Routing',     attention: false },
      { name: 'Carriers',    attention: false },
      { name: 'Returns',     attention: false },
    ],
  },
  {
    type: 'Analytics',
    agents: [
      { name: 'Spend',       attention: false },
      { name: 'Benchmarks',  attention: false },
      { name: 'Reports',     attention: false },
    ],
  },
]

// Layout constants
const W = 120
const H = 120
const CX = 60
const CY = 60
const CENTER_R = 34
const DOT_R = 5

function getBorderDotPositions(n) {
  return Array.from({ length: n }, (_, i) => {
    const angle = -Math.PI / 2 + (2 * Math.PI * i) / n
    return {
      x: CX + CENTER_R * Math.cos(angle),
      y: CY + CENTER_R * Math.sin(angle),
    }
  })
}

export default function AgentGrid({ onCategoryClick }) {
  return (
    <div style={{
      flex: 1,
      display: 'grid',
      gridTemplateColumns: 'repeat(5, 1fr)',
      gridTemplateRows: 'repeat(2, auto)',
      alignContent: 'end',
      rowGap: 4,
      paddingBottom: 24,
      paddingLeft: 16,
      position: 'relative',
      zIndex: 1,
    }}>
      {GROUPS.map((group, gi) => (
        <div key={group.type} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CategoryGroup group={group} groupIndex={gi} onCategoryClick={onCategoryClick} />
        </div>
      ))}
    </div>
  )
}

function DotWithTooltip({ agent, pos }) {
  const [hovered, setHovered] = useState(false)

  // Offset tooltip so it doesn't overlap the dot; flip if near right edge
  const tipLeft = pos.x + DOT_R + 6
  const tipTop = pos.y - 10

  return (
    <>
      <motion.div
        onHoverStart={() => setHovered(true)}
        onHoverEnd={() => setHovered(false)}
        animate={agent.attention ? { scale: [1, 1.6, 1], opacity: [1, 0.5, 1] } : {}}
        transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
        style={{
          position: 'absolute',
          left: pos.x - DOT_R,
          top: pos.y - DOT_R,
          width: DOT_R * 2,
          height: DOT_R * 2,
          borderRadius: '50%',
          background: agent.attention ? '#EB1700' : '#4ADE80',
          zIndex: 2,
          cursor: 'default',
        }}
      />
      <AnimatePresence>
        {hovered && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.12 }}
            style={{
              position: 'absolute',
              left: tipLeft,
              top: tipTop,
              background: 'rgba(15,15,15,0.95)',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 6,
              padding: '4px 8px',
              whiteSpace: 'nowrap',
              zIndex: 20,
              pointerEvents: 'none',
            }}
          >
            <span style={{ fontSize: 10, fontWeight: 600, color: '#FFFFFF' }}>{agent.name}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

function CategoryGroup({ group, groupIndex, onCategoryClick }) {
  const Icon = ICON_MAP[group.type]
  const dotPositions = getBorderDotPositions(group.agents.length)
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5, delay: 0.2 + groupIndex * 0.06 }}
      style={{ position: 'relative', width: W, height: H, overflow: 'visible' }}
    >
      {/* Center label circle */}
      <motion.div
        whileHover={{ scale: 1.08, background: 'rgba(0,0,0,0.65)', transition: { duration: 0.15 } }}
        whileTap={{ scale: 0.95 }}
        onClick={() => onCategoryClick && onCategoryClick(group)}
        style={{
          position: 'absolute',
          left: CX - CENTER_R,
          top: CY - CENTER_R,
          width: CENTER_R * 2,
          height: CENTER_R * 2,
          borderRadius: '50%',
          background: 'rgba(0,0,0,0.45)',
          border: '1px solid rgba(255,255,255,0.08)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 4,
          zIndex: 1,
          cursor: 'pointer',
        }}
      >
        <Icon size={16} />
        <span style={{
          fontSize: 7,
          fontWeight: 700,
          color: 'rgba(255,255,255,0.55)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          textAlign: 'center',
          lineHeight: 1.3,
          maxWidth: CENTER_R * 2 - 10,
        }}>
          {group.type}
        </span>
      </motion.div>

      {/* Status dots on circle border */}
      {dotPositions.map((pos, i) => {
        const agent = group.agents[i]
        return (
          <DotWithTooltip key={agent.name} agent={agent} pos={pos} />
        )
      })}
    </motion.div>
  )
}


const ICON_MAP = {
  'Purchase Orders': TruckIcon,
  'Pricing':         DollarIcon,
  'Demand Planning': ChartIcon,
  'Inventory':       BoxIcon,
  'Scheduling':      CalendarIcon,
  'Contracts':       DocumentIcon,
  'Invoicing':       ReceiptIcon,
  'Supplier':        NetworkIcon,
  'Logistics':       RouteIcon,
  'Analytics':       BarChartIcon,
}

const s = { stroke: 'white', strokeWidth: 1.3, strokeLinecap: 'round', strokeLinejoin: 'round' }

function TruckIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <rect x="1" y="5" width="8" height="6" rx="0.8" {...s} />
      <path d="M9 7h2.5L13 9v2H9V7z" {...s} />
      <circle cx="3.5" cy="12.5" r="1.3" fill="white" />
      <circle cx="11" cy="12.5" r="1.3" fill="white" />
    </svg>
  )
}

function DollarIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="6.5" {...s} />
      <line x1="8" y1="3" x2="8" y2="13" {...s} />
      <path d="M5.5 5.5C5.5 4.4 6.6 4 8 4s2.5.4 2.5 1.5S9 6.8 8 7c-1 .2-2.5.7-2.5 2S6.8 11 8 11s2.5-.4 2.5-1.5" {...s} />
    </svg>
  )
}

function ChartIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <polyline points="1.5,12 5,7.5 8.5,9.5 13,3.5" {...s} />
      <polyline points="11,3.5 13,3.5 13,5.5" {...s} />
      <line x1="1.5" y1="14" x2="14.5" y2="14" stroke="white" strokeWidth="1.3" strokeLinecap="round" opacity="0.35" />
    </svg>
  )
}

function BoxIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <path d="M8 2L14 5.2V10.8L8 14L2 10.8V5.2L8 2z" {...s} />
      <path d="M8 2v12" {...s} />
      <path d="M2 5.2L8 8.4L14 5.2" {...s} />
    </svg>
  )
}

function CalendarIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <rect x="1.5" y="3" width="13" height="11" rx="1.5" {...s} />
      <line x1="1.5" y1="7" x2="14.5" y2="7" {...s} />
      <line x1="5" y1="1.5" x2="5" y2="4.5" {...s} />
      <line x1="11" y1="1.5" x2="11" y2="4.5" {...s} />
      <circle cx="5.5" cy="10.5" r="1" fill="white" />
      <circle cx="8" cy="10.5" r="1" fill="white" />
      <circle cx="10.5" cy="10.5" r="1" fill="white" />
    </svg>
  )
}

function DocumentIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <path d="M3 2h7l3 3v9H3V2z" {...s} />
      <path d="M10 2v3h3" {...s} />
      <line x1="5" y1="8" x2="11" y2="8" {...s} />
      <line x1="5" y1="10.5" x2="11" y2="10.5" {...s} />
      <line x1="5" y1="13" x2="8" y2="13" {...s} />
    </svg>
  )
}

function ReceiptIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <path d="M3 1.5h10v13l-1.5-1-1.5 1-1.5-1-1.5 1-1.5-1-1.5 1V1.5z" {...s} />
      <line x1="5.5" y1="5" x2="10.5" y2="5" {...s} />
      <line x1="5.5" y1="7.5" x2="10.5" y2="7.5" {...s} />
      <line x1="5.5" y1="10" x2="8.5" y2="10" {...s} />
    </svg>
  )
}

function RouteIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <circle cx="2.5" cy="4" r="1.8" {...s} />
      <circle cx="13.5" cy="12" r="1.8" {...s} />
      <path d="M2.5 5.8C2.5 9 5 9 8 9s5.5 0 5.5 3.2" {...s} />
    </svg>
  )
}

function BarChartIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <rect x="1.5" y="9" width="3" height="5" rx="0.6" {...s} />
      <rect x="6.5" y="5.5" width="3" height="8.5" rx="0.6" {...s} />
      <rect x="11.5" y="2" width="3" height="12" rx="0.6" {...s} />
    </svg>
  )
}

function NetworkIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="2.5" r="1.8" {...s} />
      <circle cx="2.5" cy="12.5" r="1.8" {...s} />
      <circle cx="13.5" cy="12.5" r="1.8" {...s} />
      <line x1="6.5" y1="4" x2="3.5" y2="11" {...s} />
      <line x1="9.5" y1="4" x2="12.5" y2="11" {...s} />
      <line x1="4.3" y1="12.5" x2="11.7" y2="12.5" {...s} />
    </svg>
  )
}
